import { useContext } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { ThemeContext } from 'styled-components';
import {
  campaignTargetDropdownItems,
  campaignTypeToZoneMap,
  campaignTypeToTargetMap,
} from 'constants/campaigns';
import DatePicker from 'modules/common/components/DatePicker';
import { status } from 'constants/general';
import campaignsStrings from 'modules/campaigns/strings';
import DropdownMenu from 'modules/common/components/DropdownMenu';
import MultiSelectionDropdownMenu from 'modules/common/components/MultiSelectionDropdownMenu';
import Loader from 'modules/common/components/Loader';
import SectionDivider from 'modules/common/components/SectionDivider';

import {
  LoaderWrapper,
  AddCampaignWrapper,
  FormWrapper,
  FormTitle,
  FormGrid,
  FormLabel,
  FormTextInput,
  FormFieldWrapper,
  FormFieldMultiInputWrapper,
  FormValidationError,
  AddTargetBtnWrapper,
  AddTargetBtn,
  RemoveTargetBtn,
  FormBtnWrapper,
  FormBtn,
} from './AddCampaignComponent.style';

const AddCampaignComponent = ({
  shouldDisableFormFields,
  formFieldsValues,
  formFieldsValidation,
  handleTextInputFieldChange,
  handleDropdownFieldChange,
  handleMultiSelectionDropdownFieldChange,
  handleDatePickerFieldChange,
  handleTargetFieldChange,
  handleAddTargetBtnClick,
  handleDeleteTargetBtnClick,
  handleAddOrEditCampaignBtnClick,
}) => {
  const { staticData, addOrEditCampaign } = useSelector(state => ({
    staticData: state.common.staticData,
    addOrEditCampaign: state.campaigns.addOrEditCampaign,
  }));

  const themeContext = useContext(ThemeContext);
  const { colors } = themeContext;

  const { id: campaignKey } = useParams();

  const formBtnText =
    (campaignKey && campaignsStrings.addCampaign.formEditBtnText) ||
    campaignsStrings.addCampaign.formAddBtnText;

  const typeDropdownData = staticData.details?.data?.campaignTypes?.map(type => ({
    key: type.campaignTypeId,
    name: type.campaignTypeName,
  }));
  const billingDropdownData = staticData.details?.data?.pricingModels?.map(billing => ({
    key: billing.pricingModelId,
    name: billing.pricingModelName,
  }));

  const selectedTypeKey = formFieldsValues.type.key;
  let zoneDropdownData = [];
  if (selectedTypeKey)
    zoneDropdownData = staticData.details?.data?.zones
      ?.filter(zone => campaignTypeToZoneMap[selectedTypeKey]?.includes(zone.zoneKey))
      .map(zone => ({
        key: zone.zoneKey,
        name: zone.zoneName,
      }));

  let targetDropdownData = [];
  if (selectedTypeKey)
    targetDropdownData = campaignTargetDropdownItems.filter(target =>
      campaignTypeToTargetMap[selectedTypeKey]?.includes(target.key),
    );

  if (staticData.status === status.SHOULD_CALL_API || staticData.status === status.FETCHING)
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );

  return (
    <AddCampaignWrapper>
      <FormWrapper>
        <FormTitle>{campaignsStrings.addCampaign.formTitle}</FormTitle>
        <SectionDivider gap={16} withSeparator separatorGap={16} borderColor={colors.grey004} />
        <FormGrid>
          {/* Name Field */}
          {shouldDisableFormFields === false && (
            <FormFieldWrapper>
              <FormLabel>{campaignsStrings.addCampaign.formNameFieldLabel}</FormLabel>
              <SectionDivider gap={8} />
              <FormTextInput
                type="text"
                value={formFieldsValues.name}
                onChange={event => handleTextInputFieldChange('name', event)}
              />
              {formFieldsValidation.name === false && (
                <>
                  <SectionDivider gap={4} />
                  <FormValidationError>
                    {campaignsStrings.addCampaign.formFieldErrorText(
                      campaignsStrings.addCampaign.formNameFieldLabel,
                    )}
                  </FormValidationError>
                </>
              )}
            </FormFieldWrapper>
          )}

          {/* Type Field */}
          {shouldDisableFormFields === false && (
            <FormFieldWrapper>
              <FormLabel>{campaignsStrings.addCampaign.formTypeFieldLabel}</FormLabel>
              <SectionDivider gap={8} />
              <DropdownMenu
                dropdownMenuItemsList={typeDropdownData}
                dropdownMenuSelectedObj={formFieldsValues.type}
                dropdownMenuPlaceholder={campaignsStrings.addCampaign.formTypeFieldPlaceholder}
                showLoader={false}
                handleDropdownMenuChange={value => handleDropdownFieldChange('type', value)}
              />
              {formFieldsValidation.type === false && (
                <>
                  <SectionDivider gap={4} />
                  <FormValidationError>
                    {campaignsStrings.addCampaign.formFieldErrorText(
                      campaignsStrings.addCampaign.formTypeFieldLabel,
                    )}
                  </FormValidationError>
                </>
              )}
            </FormFieldWrapper>
          )}

          {/* Billing Field */}
          {shouldDisableFormFields === false && (
            <FormFieldWrapper>
              <FormLabel>{campaignsStrings.addCampaign.formBillingFieldLabel}</FormLabel>
              <SectionDivider gap={8} />
              <DropdownMenu
                dropdownMenuItemsList={billingDropdownData}
                dropdownMenuSelectedObj={formFieldsValues.billing}
                dropdownMenuPlaceholder={campaignsStrings.addCampaign.formBillingFieldPlaceholder}
                showLoader={false}
                handleDropdownMenuChange={value => handleDropdownFieldChange('billing', value)}
              />
              {formFieldsValidation.billing === false && (
                <>
                  <SectionDivider gap={4} />
                  <FormValidationError>
                    {campaignsStrings.addCampaign.formFieldErrorText(
                      campaignsStrings.addCampaign.formBillingFieldLabel,
                    )}
                  </FormValidationError>
                </>
              )}
            </FormFieldWrapper>
          )}

          {/* Zone Field */}
          {shouldDisableFormFields === false && (
            <FormFieldWrapper>
              <FormLabel>{campaignsStrings.addCampaign.formZoneFieldLabel}</FormLabel>
              <SectionDivider gap={8} />
              <MultiSelectionDropdownMenu
                dropdownMenuItemsList={zoneDropdownData}
                dropdownMenuSelectedArr={formFieldsValues.zone}
                dropdownMenuPlaceholder={campaignsStrings.addCampaign.formZoneFieldPlaceholder}
                handleDropdownMenuChange={value =>
                  handleMultiSelectionDropdownFieldChange('zone', value)
                }
              />
              {formFieldsValidation.zone === false && (
                <>
                  <SectionDivider gap={4} />
                  <FormValidationError>
                    {campaignsStrings.addCampaign.formFieldErrorText(
                      campaignsStrings.addCampaign.formZoneFieldLabel,
                    )}
                  </FormValidationError>
                </>
              )}
            </FormFieldWrapper>
          )}

          {/* Start Date Field */}
          {shouldDisableFormFields === false && (
            <FormFieldWrapper>
              <FormLabel>{campaignsStrings.addCampaign.formStartDateFieldLabel}</FormLabel>
              <SectionDivider gap={8} />
              <DatePicker
                selectedDate={formFieldsValues.startDate}
                handleDatePickerValueChange={date => handleDatePickerFieldChange('startDate', date)}
              />
              {formFieldsValidation.startDate === false && (
                <>
                  <SectionDivider gap={4} />
                  <FormValidationError>
                    {campaignsStrings.addCampaign.formFieldErrorText(
                      campaignsStrings.addCampaign.formStartDateFieldLabel,
                    )}
                  </FormValidationError>
                </>
              )}
            </FormFieldWrapper>
          )}

          {/* End Date Field */}
          <FormFieldWrapper>
            <FormLabel>{campaignsStrings.addCampaign.formEndDateFieldLabel}</FormLabel>
            <SectionDivider gap={8} />
            <DatePicker
              selectedDate={formFieldsValues.endDate}
              handleDatePickerValueChange={date => handleDatePickerFieldChange('endDate', date)}
            />
            {formFieldsValidation.endDate === false && (
              <>
                <SectionDivider gap={4} />
                <FormValidationError>
                  {campaignsStrings.addCampaign.formFieldErrorText(
                    campaignsStrings.addCampaign.formEndDateFieldLabel,
                  )}
                </FormValidationError>
              </>
            )}
          </FormFieldWrapper>

          {/* Target Field */}
          {shouldDisableFormFields === false && (
            <>
              {formFieldsValues.target.map((target, index) => (
                <FormFieldWrapper key={target.id} span={2} cardStyle>
                  <FormLabel>{campaignsStrings.addCampaign.formTargetFieldLabel}</FormLabel>
                  <SectionDivider gap={8} />
                  <DropdownMenu
                    dropdownMenuItemsList={targetDropdownData}
                    dropdownMenuSelectedObj={target.type || {}}
                    dropdownMenuPlaceholder={
                      campaignsStrings.addCampaign.formTargetFieldPlaceholder
                    }
                    showLoader={false}
                    handleDropdownMenuChange={value =>
                      handleTargetFieldChange('type', value, index)
                    }
                  />
                  <SectionDivider gap={12} />
                  <FormFieldMultiInputWrapper>
                    <FormFieldWrapper flexBasis={30}>
                      <FormTextInput
                        type="text"
                        value={target.key}
                        placeholder={campaignsStrings.addCampaign.formTargetKeyFieldPlaceholder}
                        onChange={event =>
                          handleTargetFieldChange('key', event.target.value, index)
                        }
                      />
                      {index === 0 && formFieldsValidation.targetKey === false && (
                        <>
                          <SectionDivider gap={4} />
                          <FormValidationError>
                            {campaignsStrings.addCampaign.formFieldErrorText(
                              campaignsStrings.addCampaign.formTargetKeyFieldPlaceholder,
                            )}
                          </FormValidationError>
                        </>
                      )}
                    </FormFieldWrapper>
                    <FormFieldWrapper flexGrow={1}>
                      <FormTextInput
                        type="text"
                        value={target.value}
                        placeholder={campaignsStrings.addCampaign.formTargetValueFieldPlaceholder}
                        onChange={event =>
                          handleTargetFieldChange('value', event.target.value, index)
                        }
                      />
                      {index === 0 && formFieldsValidation.targetValue === false && (
                        <>
                          <SectionDivider gap={4} />
                          <FormValidationError>
                            {campaignsStrings.addCampaign.formFieldErrorText(
                              campaignsStrings.addCampaign.formTargetValueFieldPlaceholder,
                            )}
                          </FormValidationError>
                        </>
                      )}
                    </FormFieldWrapper>
                  </FormFieldMultiInputWrapper>
                  {index !== 0 && (
                    <RemoveTargetBtn onClick={() => handleDeleteTargetBtnClick(index)} />
                  )}
                </FormFieldWrapper>
              ))}
            </>
          )}
        </FormGrid>
        {shouldDisableFormFields === false && (
          <>
            <SectionDivider gap={10} />
            <AddTargetBtnWrapper>
              <AddTargetBtn as="button" onClick={handleAddTargetBtnClick}>
                {campaignsStrings.addCampaign.formAddTargetBtnText}
              </AddTargetBtn>
            </AddTargetBtnWrapper>
          </>
        )}
        <SectionDivider gap={35} />
        <FormBtnWrapper>
          <FormBtn as="button" onClick={handleAddOrEditCampaignBtnClick}>
            {(addOrEditCampaign.status === status.FETCHING && (
              <Loader width={14} height={14} thickness={4} />
            )) ||
              formBtnText}
          </FormBtn>
        </FormBtnWrapper>
      </FormWrapper>
    </AddCampaignWrapper>
  );
};

AddCampaignComponent.propTypes = {
  shouldDisableFormFields: PropTypes.bool.isRequired,
  formFieldsValues: PropTypes.shape({
    name: PropTypes.string,
    type: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
    billing: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
    zone: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string])),
    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date),
    target: PropTypes.arrayOf(PropTypes.shape()),
  }).isRequired,
  formFieldsValidation: PropTypes.shape({
    name: PropTypes.bool,
    type: PropTypes.bool,
    billing: PropTypes.bool,
    zone: PropTypes.bool,
    startDate: PropTypes.bool,
    endDate: PropTypes.bool,
    targetKey: PropTypes.bool,
    targetValue: PropTypes.bool,
  }).isRequired,
  handleTextInputFieldChange: PropTypes.func.isRequired,
  handleDropdownFieldChange: PropTypes.func.isRequired,
  handleMultiSelectionDropdownFieldChange: PropTypes.func.isRequired,
  handleDatePickerFieldChange: PropTypes.func.isRequired,
  handleTargetFieldChange: PropTypes.func.isRequired,
  handleAddTargetBtnClick: PropTypes.func.isRequired,
  handleDeleteTargetBtnClick: PropTypes.func.isRequired,
  handleAddOrEditCampaignBtnClick: PropTypes.func.isRequired,
};

export default AddCampaignComponent;
